/**
 * Áudio e vídeo (docs/spec/settings.md): o teste rápido (a prévia da câmera e o medidor do microfone)
 * e as listas de aparelhos, com a saída das vozes da chamada (`setSinkId`, que nem todo navegador
 * tem: sem ele, a linha some).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/index.js';
import { useApp } from '../store.js';
import { Button, ComboBox } from '../ui/controls.js';
import { toast } from '../ui/Toaster.js';

/** O navegador deixa escolher a saída de som de um `<audio>`? (o Safari não deixa) */
export function supportsSpeakerChoice(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

/** Os aparelhos de mídia; a lista volta a ser lida quando um aparelho entra ou sai, e com `reload`. */
export function useMediaDevices(): { devices: MediaDeviceInfo[]; reload: () => void } {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const reload = useCallback(() => {
    void navigator.mediaDevices
      ?.enumerateDevices()
      .then(setDevices)
      .catch(() => setDevices([]));
  }, []);
  useEffect(() => {
    reload();
    navigator.mediaDevices?.addEventListener?.('devicechange', reload);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', reload);
  }, [reload]);
  return { devices, reload };
}

/** Avisa as listas de aparelhos que os nomes já podem aparecer (a permissão saiu num teste). */
const DEVICES_EVENT = 'rpgdesk:media-permission';

/**
 * A lista de um tipo de aparelho, com "Padrão do sistema" primeiro. Sem permissão, o navegador não dá
 * os nomes: fica "Microfone 1", "Microfone 2"…
 */
export function DeviceSelect({ id, kind, value, onChange, fallbackLabel, describedBy }: { id: string; kind: MediaDeviceKind; value: string; onChange: (value: string) => void; fallbackLabel: string; describedBy?: string }) {
  const t = useT();
  const { devices, reload } = useMediaDevices();
  useEffect(() => {
    window.addEventListener(DEVICES_EVENT, reload);
    return () => window.removeEventListener(DEVICES_EVENT, reload);
  }, [reload]);
  // "default" e "communications" são apelidos do Chrome para um aparelho que já está na lista
  const list = devices.filter((d) => d.kind === kind && d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications');
  const options = [{ value: '', label: t('config.conf.default') }, ...list.map((d, i) => ({ value: d.deviceId, label: d.label || `${fallbackLabel} ${i + 1}` }))];
  // um aparelho que sumiu continua escolhido, com o nome que dá para mostrar
  if (value && !options.some((o) => o.value === value)) options.push({ value, label: t('prefs.av.missing') });
  return (
    <span className="rd-prefs__device">
      <ComboBox id={id} value={value} onChange={onChange} options={options} {...(describedBy ? { 'aria-describedby': describedBy } : {})} />
    </span>
  );
}

/** Um tom curto (WAV em memória), para ouvir a saída escolhida. */
function testToneUrl(): string {
  const rate = 22050;
  const samples = Math.round(rate * 0.45);
  const data = new DataView(new ArrayBuffer(44 + samples * 2));
  const text = (offset: number, s: string) => [...s].forEach((c, i) => data.setUint8(offset + i, c.charCodeAt(0)));
  text(0, 'RIFF');
  data.setUint32(4, 36 + samples * 2, true);
  text(8, 'WAVEfmt ');
  data.setUint32(16, 16, true);
  data.setUint16(20, 1, true);
  data.setUint16(22, 1, true);
  data.setUint32(24, rate, true);
  data.setUint32(28, rate * 2, true);
  data.setUint16(32, 2, true);
  data.setUint16(34, 16, true);
  text(36, 'data');
  data.setUint32(40, samples * 2, true);
  for (let i = 0; i < samples; i += 1) {
    // dois tons (lá e mi), com entrada e saída suaves
    const fade = Math.min(1, i / 600, (samples - i) / 1800);
    const freq = i < samples / 2 ? 660 : 880;
    data.setInt16(44 + i * 2, Math.sin((2 * Math.PI * freq * i) / rate) * 0.35 * fade * 32767, true);
  }
  return URL.createObjectURL(new Blob([data.buffer], { type: 'audio/wav' }));
}

/** Toca o tom de teste pela saída escolhida (vazio: a do sistema). */
export async function playOnSpeaker(deviceId: string, volume: number): Promise<void> {
  const url = testToneUrl();
  const audio = new Audio(url) as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
  audio.volume = Math.max(0, Math.min(1, volume));
  audio.addEventListener('ended', () => URL.revokeObjectURL(url), { once: true });
  if (deviceId && audio.setSinkId) await audio.setSinkId(deviceId);
  await audio.play();
}

/** O teste rápido: a prévia da câmera escolhida e o medidor do microfone escolhido. */
export function AvTest() {
  const t = useT();
  const settings = useApp((s) => s.settings);
  const video = useRef<HTMLVideoElement>(null);
  const [camera, setCamera] = useState<MediaStream | null>(null);
  const [level, setLevel] = useState<number | null>(null);
  const stopMic = useRef<(() => void) | null>(null);
  /** a seção está na tela: a permissão que chega depois de sair não liga nada */
  const mounted = useRef(true);
  /** um pedido de câmera ou de microfone em andamento: o segundo clique não pede outro */
  const asking = useRef({ camera: false, mic: false });

  // sair da seção desliga a câmera e o microfone
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stopMic.current?.();
    };
  }, []);
  useEffect(() => () => camera?.getTracks().forEach((track) => track.stop()), [camera]);
  useEffect(() => {
    if (video.current) video.current.srcObject = camera;
  }, [camera]);

  const toggleCamera = async () => {
    if (camera) {
      setCamera(null);
      return;
    }
    if (asking.current.camera) return;
    asking.current.camera = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: settings.cameraDeviceId ? { deviceId: settings.cameraDeviceId } : true });
      if (!mounted.current) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      setCamera(stream);
      window.dispatchEvent(new Event(DEVICES_EVENT));
    } catch {
      if (mounted.current) toast(t('prefs.av.camera.error'), 'error');
    } finally {
      asking.current.camera = false;
    }
  };

  const toggleMic = async () => {
    if (stopMic.current) {
      stopMic.current();
      return;
    }
    if (asking.current.mic) return;
    asking.current.mic = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: settings.micDeviceId ? { deviceId: settings.micDeviceId } : true });
      if (!mounted.current) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      window.dispatchEvent(new Event(DEVICES_EVENT));
      const ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      let raf = 0;
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (const v of data) peak = Math.max(peak, Math.abs(v - 128) / 128);
        setLevel(peak);
        raf = requestAnimationFrame(tick);
      };
      tick();
      stopMic.current = () => {
        cancelAnimationFrame(raf);
        for (const track of stream.getTracks()) track.stop();
        void ctx.close();
        stopMic.current = null;
        setLevel(null);
      };
    } catch {
      if (mounted.current) toast(t('voice.error.mic-failed'), 'error');
    } finally {
      asking.current.mic = false;
    }
  };

  // 16 barrinhas: verdes, depois amarelas, depois vermelhas
  const lit = Math.round(Math.min(1, (level ?? 0) * 2.2) * 16);
  return (
    <div className="rd-prefs__avTest">
      <div className="rd-prefs__camera">
        <video ref={video} autoPlay muted playsInline hidden={!camera} aria-label={t('prefs.av.camera.preview')} />
        {camera ? null : <span className="rd-prefs__cameraOff">{t('prefs.av.camera.off')}</span>}
        <Button variant="secondary" icon={camera ? 'cameraOff' : 'camera'} onClick={() => void toggleCamera()}>
          {camera ? t('prefs.av.camera.stop') : t('prefs.av.camera.start')}
        </Button>
      </div>
      <div className="rd-prefs__mic">
        <span className="rd-prefs__label">{t('config.conf.mic')}</span>
        <div className="rd-prefs__meter" role="meter" aria-label={t('prefs.av.mic.level')} aria-valuemin={0} aria-valuemax={16} aria-valuenow={lit}>
          {Array.from({ length: 16 }, (_, i) => (
            <span key={i} className={i < lit ? (i < 10 ? 'is-ok' : i < 13 ? 'is-high' : 'is-peak') : undefined} />
          ))}
        </div>
        <div>
          <Button variant="secondary" icon="mic" onClick={() => void toggleMic()}>
            {level === null ? t('config.conf.test') : t('config.conf.stopTest')}
          </Button>
        </div>
        <p className="rd-prefs__desc">{level === null ? t('prefs.av.mic.idle') : t('prefs.av.mic.live')}</p>
      </div>
    </div>
  );
}
